import { FC, ReactNode } from 'react';

export type FCC<T = Record<string, any>> = FC<T & { children?: ReactNode }>;
export type GenericFunction = (...args: any[]) => any;

export enum STEP {
  DONATION = 'CheckoutStep.DONATION',
  SUPPORTER = 'CheckoutStep.SUPPORTER',
  PAYMENT = 'CheckoutStep.PAYMENT',
  THANK_YOU = 'CheckoutStep.THANK_YOU',
}

export enum CHECKOUT_STATE {
  OPEN = 'OPEN',
  COMPLETED = 'COMPLETED',
  CLOSED = 'CLOSED',
}

export enum LEGAL_PERSON {
  COMPANY = 'COMPANY',
  INDIVIDUAL = 'INDIVIDUAL',
}

/** @see {isCheckoutParams} ts-auto-guard:type-guard */
export interface CheckoutParams {
  co: string;
}

export enum DONATION_STATE {
  STARTED = 'STARTED',
  VERIFIED = 'VERIFIED',
  COMPLETED = 'COMPLETED',
}

export enum DONATION_FREQUENCY {
  ONEOFF = 0,
  MONTHLY = 12,
  // ANNUALLY = 1, unused
}

export type ServerField =
  | 'first_name'
  | 'last_name'
  | 'business_name'
  | 'email'
  | 'phone'
  | 'mobile'
  | 'address'
  | 'city'
  | 'country'
  | 'postal_code'
  | 'locality'
  | 'ssn'
  | 'vat';

export interface Organization {
  name: string;
  logo: string | null;
  privacy_policy: string | null;
  is_development: boolean;
  tos: string | null;
}

export interface Campaign {
  id: number;
  title: string;
  slug: string;
  cover_url: string | null;
  sitefront_url: string;
  description: string | null;

  has_one_off: boolean;
  has_subscription: boolean;
  allow_custom_amount: boolean;
  allow_custom_subscription_amount: boolean;
  default_amount?: number | null;
  default_subscription_amount?: number | null;
  min_amount?: number | null;
  max_amount?: number | null;
  min_subscription_amount?: number | null;
  max_subscription_amount?: number | null;
  asks: Array<number>;
  asks_subscription: Array<number>;
  shown_fields: Array<ServerField>;
  required_fields: Array<ServerField>;

  privacy_fields: Array<{
    id: number;
    code: string;
    label: string;
    message: string;
    is_default: boolean;
    is_required: boolean;
  }>;
}

export interface PeerCampaign {
  supporter_name: string;
  supporter_image_src: string | null;
  supporter_message: string | null;
}

export interface CostExample {
  uuid: string;
  amount: number;
  image: string | null;
  name: string;
  description: string;
  monitor_quantity: boolean;
  quantity: number;
  available_quantity: number;
}

/** @see {isCheckoutConfig} ts-auto-guard:type-guard */
export interface Checkout {
  token: string;
  step: STEP;
  state: CHECKOUT_STATE;
  completed_date: string | null;

  campaign: Campaign;
  organization: Organization;
  peer_campaign: PeerCampaign | null;
  cost_example: CostExample | null;
  enabled_payment_methods: PaymentMethods;

  supporter_id: number | null;
  donation_id: number | null;

  amount: number | null;
  frequency: DONATION_FREQUENCY | null;
  legal_person: LEGAL_PERSON | null;
  business_name: string | null;
  first_name: string | null;
  last_name: string | null;
  vat: string | null;
  ssn: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  address: string | null;
  city: string | null;
  locality: string | null;
  postal_code: string | null;
  country: string | null;

  privacy_fields: Array<string>;
}

export interface FormValues {
  legalPerson: LEGAL_PERSON;

  amount: number;
  frequency: DONATION_FREQUENCY;

  businessName: string;
  vat: string;

  firstName: string;
  lastName: string;
  ssn: string;

  email: string;
  phone: string;
  mobile: string;
  address: string;
  city: string;
  locality: string;
  postalCode: string;
  country: string;

  paymentMethod: PAYMENT_METHOD_TYPE;
  paymentMethodId: number;

  acceptedPrivacyFields: Array<string>; // codes
}

export enum PAYMENT_METHOD_TYPE {
  STRIPE = 'STRIPE',
  PAYPAL = 'PAYPAL',
  SATISPAY = 'SATISPAY',
  MANUAL = 'MANUAL',
}

export enum STRIPE_PAYMENT_TYPE {
  SEPA_DIRECT_DEBIT = 'sepa_debit',
  CREDIT_CARD = 'card',
  POS = 'card_present',
}

export type StripePM = {
  id: number;
  label: string | null;
  type: PAYMENT_METHOD_TYPE.STRIPE;
  has_oneoff_support: boolean;
  has_subscription_support: boolean;
  stripe_payment_type: STRIPE_PAYMENT_TYPE;
  stripe_account_id: string;
  stripe_pub_key: string;
};

export type PaypalPM = {
  id: number;
  label: string | null;
  type: PAYMENT_METHOD_TYPE.PAYPAL;
  has_oneoff_support: boolean;
  has_subscription_support: boolean;
};

export type SatispayPM = {
  id: number;
  label: string | null;
  type: PAYMENT_METHOD_TYPE.SATISPAY;
  has_oneoff_support: boolean;
  has_subscription_support: boolean;
};

export type ManualPM = {
  id: number;
  label: string | null;
  type: PAYMENT_METHOD_TYPE.MANUAL;
  has_oneoff_support: boolean;
  has_subscription_support: boolean;
  manual_description: string | null;
};

/** @see {isServerError} ts-auto-guard:type-guard */
export type ServerError = {
  code: string;
  message?: string;
};

export type PaymentMethod = StripePM | PaypalPM | ManualPM | SatispayPM;

export type PaymentMethods = Array<PaymentMethod>;
